import {
  Component,
  ErrorHandler,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {ConfirmationService, MessageService} from 'primeng/api';
import {Subject, takeUntil} from 'rxjs';
import {
  EditableOfferRule,
  NormalizedWritableOfferRule,
  OfferRuleChangeSetValidation,
  ruleChangeStates,
  RuleChangeState
} from '../../../../../model/explicit-save-offer-rule.model';
import {BulkOfferRuleResponse} from '../../../../../model/bulk-offer-rule.model';
import {OfferRuleModel} from '../../../../../model/offer-rule.model';
import {OfferRuleTypeEnumerator} from '../enumerators/offer-rule-type.enumerator';
import {EnhancedRuleDraft} from '../models/enhanced-rule-draft.model';
import {
  OfferRuleWorkflowAggregateCollection,
  OfferRuleWorkflowAggregateItem,
  OfferRuleWorkflowCommandStatusResponse,
  OfferRuleWorkflowControlledError,
  OfferRuleWorkflowOperations
} from '../models/offer-rule-workflow-api.model';
import {
  IpopRuleWorkflowModalMode
} from '../rule-modal/ipop-rule-workflow-rule-modal.component';
import {
  areEnhancedRuleDraftsEqual,
  cloneEnhancedRuleDraft,
  cloneOfferRule,
  mapAggregateItemToEnhancedRuleDraft,
  mapDraftToCreateOperation,
  mapDraftToDeleteOperation,
  mapDraftToUpdateOperation,
  normalizeEnhancedRuleDraft
} from '../utilities/offer-rule-workflow.mapper';
import {OfferRuleWorkflowService} from '../services/offer-rule-workflow.service';

interface RuleTypeFilterOption {
  readonly label: string;
  readonly value: OfferRuleTypeFilter;
}

type OfferRuleTypeFilter = OfferRuleTypeEnumerator | 'all';

const persistedClientKeyPrefix = 'persisted-';
const draftClientKeyPrefix = 'draft-';
const maximumBulkOperations = 50;
const uuidByteLength = 16;
const uuidVersionIndex = 6;
const uuidVariantIndex = 8;
const uuidVersionMask = 15;
const uuidVersionValue = 64;
const uuidVariantMask = 63;
const uuidVariantValue = 128;
const uuidSegmentLengths = [4, 2, 2, 2, 6];
const allRuleTypes: OfferRuleTypeFilter = 'all';
const createMode: IpopRuleWorkflowModalMode = 'create';
const editMode: IpopRuleWorkflowModalMode = 'edit';
const activeStatus = 'Active';
const genericLoadError = 'Unable to load offer rules.';
const genericStageError = 'The rule could not be staged. Review the rule and try again.';
const deleteConfirmationMessage = 'Are you sure you want to delete this rule?';
const focusDelayMilliseconds = 0;
const controlledErrorMessages: Readonly<Record<OfferRuleWorkflowControlledError['code'], string>> = {
  invalid_command_parameters: 'The save command parameters were invalid.',
  not_found: 'A staged rule no longer exists for this account.',
  validation_failed: 'A staged rule did not pass server validation.',
  aggregate_version_conflict: 'A staged rule changed on the server.',
  aggregate_integrity_conflict: 'The current aggregate state could not be updated safely.',
  persistence_failed: 'The rules could not be saved.',
  publication_failed: 'The save command could not be dispatched. Retry the save.',
  disclosure_sync_failed_manual_intervention: 'Disclosure synchronization requires manual operational correction.',
  processing_failed_manual_intervention: 'Registry processing requires manual operational correction.',
  worker_terminated: 'The save stopped unexpectedly. Reload the rules before retrying.'
};
const operationKindLabels: Readonly<Record<'create' | 'update' | 'delete', string>> = {
  create: 'create',
  update: 'update',
  delete: 'delete'
};

@Component({
  selector: 'auto-ipop-rule-workflow-rules-list',
  templateUrl: './ipop-rule-workflow-rules-list.component.html',
  styleUrls: ['./ipop-rule-workflow-rules-list.component.scss'],
  standalone: false
})
export class IpopRuleWorkflowRulesListComponent implements OnInit, OnChanges, OnDestroy
{
  @Input() accountId = 0;
  @Input() isOfferRegistryProcessingActive = false;
  @Input() isProcessingRules = false;
  @Input() isSavingRules = false;
  @Input() areRuleActionsExternallyDisabled = false;
  @Input() isProcessRulesButtonDisabled = false;

  @Output() processRulesRequested = new EventEmitter<void>();
  @Output() saveRulesRequested = new EventEmitter<OfferRuleWorkflowOperations>();
  @Output() hasUnsavedRuleChangesChange = new EventEmitter<boolean>();

  readonly ruleChangeStates = ruleChangeStates;
  readonly ruleTypeFilterOptions: RuleTypeFilterOption[] = [
    {label: 'All', value: allRuleTypes},
    {label: 'Offer Set Rules', value: OfferRuleTypeEnumerator.OfferSetRule},
    {label: 'Dollar Discounts', value: OfferRuleTypeEnumerator.DollarDiscountRule}
  ];

  publicRules: EditableOfferRule[] = [];
  privateRules: EditableOfferRule[] = [];
  filteredPublicRules: EditableOfferRule[] = [];
  filteredPrivateRules: EditableOfferRule[] = [];
  searchValue = '';
  selectedRuleTypeFilter: OfferRuleTypeFilter = allRuleTypes;
  tableLoading = false;
  hasLoaded = false;
  hasRuleLoadFailed = false;
  hasUnsavedRuleChanges = false;
  commandError: OfferRuleWorkflowControlledError | null = null;
  hasVersionConflict = false;
  changeSet: OfferRuleWorkflowOperations = this.createEmptyChangeSet();
  changeSetValidation: OfferRuleChangeSetValidation = this.createInitialValidation();

  dialogVisible = false;
  dialogTitle = '';
  dialogMode: IpopRuleWorkflowModalMode = createMode;
  selectedRule: OfferRuleModel | null = null;
  selectedDraft: EnhancedRuleDraft | null = null;

  private baselineById = new Map<number, EnhancedRuleDraft>();
  private workingRowsByClientKey = new Map<string, EditableOfferRule>();
  private envelopesByClientKey = new Map<string, EnhancedRuleDraft>();
  private conflictSnapshot: readonly EnhancedRuleDraft[] = [];
  private selectedClientKey: string | null = null;
  private loadedAccountId: number | null = null;
  private accountLoadSequence = 0;
  private modalLaunchElement: HTMLElement | null = null;
  private destroySubject = new Subject<void>();

  constructor(
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private offerRuleWorkflowService: OfferRuleWorkflowService,
    private errorHandler: ErrorHandler
  )
  {
  }

  /** Initializes account-scoped rule state. */
  ngOnInit(): void
  {
    this.handleAccountChange(this.accountId);
  }

  /** Responds to account replacement and action-lock changes. */
  ngOnChanges(changes: SimpleChanges): void
  {
    const accountChange = changes['accountId'];
    if (accountChange && !accountChange.firstChange) {
      this.handleAccountChange(accountChange.currentValue);
    }
    if (this.getRuleMutationDisabled() && this.dialogVisible) {
      this.closeDialog();
    }
  }

  /** Invalidates outstanding loads and releases subscriptions. */
  ngOnDestroy(): void
  {
    this.accountLoadSequence += 1;
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  /** Requests processing when the parent action is available. */
  requestProcessRules(): void
  {
    if (!this.isProcessRulesButtonDisabled) {
      this.processRulesRequested.emit();
    }
  }

  /** Emits an isolated valid change-set snapshot. */
  requestSaveRules(): void
  {
    if (!this.getSaveRulesButtonDisabled()) {
      this.saveRulesRequested.emit(this.cloneChangeSet(this.changeSet));
    }
  }

  /** Reports whether the current local state can be persisted. */
  getSaveRulesButtonDisabled(): boolean
  {
    const invalidAccount = !this.isValidAccountId(this.accountId);
    const isDisabled = !this.hasLoaded
      || !this.hasUnsavedRuleChanges
      || !this.changeSetValidation.isValid
      || this.changeSetValidation.operationCount > maximumBulkOperations
      || this.getRuleMutationDisabled()
      || invalidAccount;

    return isDisabled;
  }

  /** Reports whether list mutations are externally locked. */
  getRuleMutationDisabled(): boolean
  {
    const isDisabled = this.tableLoading
      || this.hasRuleLoadFailed
      || !this.hasLoaded
      || this.isOfferRegistryProcessingActive
      || this.isProcessingRules
      || this.isSavingRules
      || this.areRuleActionsExternallyDisabled;

    return isDisabled;
  }

  /** Replaces state from a verified completed command result. */
  reconcileCompletedStatus(response: OfferRuleWorkflowCommandStatusResponse): boolean
  {
    const expectedAccount = response.accountId === this.accountId && this.loadedAccountId === this.accountId;
    const expectedTerminal = response.status === 'completed'
      && response.phase === 'completed'
      && response.checkpoint?.persistenceCommitted === true;
    const mappingsValid = this.areTerminalMappingsComplete(response);
    let wasReplaced = false;
    if (expectedAccount && expectedTerminal && mappingsValid) {
      this.replaceAuthoritativeItems(response.items);
      this.commandError = null;
      this.hasVersionConflict = false;
      this.conflictSnapshot = [];
      wasReplaced = true;
    }

    return wasReplaced;
  }

  /** Clears the dirty warning after persistence is authoritatively confirmed. */
  markRulesSaved(): void
  {
    this.hasUnsavedRuleChanges = false;
    this.hasUnsavedRuleChangesChange.emit(false);
  }

  /** Preserves a controlled pre-commit failure for correction. */
  applyPreCommitFailure(error: OfferRuleWorkflowControlledError): void
  {
    this.commandError = error;
  }

  /** Presents allow-listed controlled error text without server exception detail. */
  getCommandErrorMessage(error: OfferRuleWorkflowControlledError): string
  {
    let message = controlledErrorMessages[error.code];
    const operation = error.operation;
    if (operation !== undefined && operation !== null) {
      const operationLabel = operationKindLabels[operation.kind];
      message = `${message} Review the affected ${operationLabel} operation.`;
    }

    return message;
  }

  /** Preserves staged data separately and reloads after a version conflict. */
  handleVersionConflict(error: OfferRuleWorkflowControlledError): void
  {
    this.conflictSnapshot = Array.from(this.envelopesByClientKey.values())
      .map((draft) => cloneEnhancedRuleDraft(draft));
    this.commandError = error;
    this.hasVersionConflict = true;
    this.reloadAuthoritativeAggregates();
  }

  /** Discards committed staging and reloads versions that downstream work may have changed. */
  reconcileCommittedFailure(response: OfferRuleWorkflowCommandStatusResponse): void
  {
    this.commandError = response.error;
    this.replaceAuthoritativeItems(response.items);
    this.reloadAuthoritativeAggregates();
  }

  /** Reloads the enhanced aggregate boundary without submitting mutations. */
  reloadAuthoritativeAggregates(): void
  {
    if (this.isValidAccountId(this.accountId)) {
      this.accountLoadSequence += 1;
      this.loadRules(this.accountId, this.accountLoadSequence);
    }
  }

  /** Requires exact submitted create and delete mappings before reconciliation. */
  private areTerminalMappingsComplete(response: OfferRuleWorkflowCommandStatusResponse): boolean
  {
    const createKeys = this.changeSet.creates.map((operation) => operation.clientKey);
    const mappedKeys = response.created.map((mapping) => mapping.clientKey);
    const deletedIds = this.changeSet.deletes.map((operation) => operation.ruleId);
    const mappedDeletedIds = response.deleted.map((mapping) => mapping.ruleId);
    const uniqueCreateMappings = new Set(mappedKeys).size === mappedKeys.length;
    const uniqueDeleteMappings = new Set(mappedDeletedIds).size === mappedDeletedIds.length;
    const createsMatch = createKeys.length === mappedKeys.length
      && createKeys.every((clientKey) => mappedKeys.includes(clientKey));
    const deletesMatch = deletedIds.length === mappedDeletedIds.length
      && deletedIds.every((ruleId) => mappedDeletedIds.includes(ruleId));
    const identitiesMatch = response.created.every((mapping) => this.isCreatedMappingAuthoritative(mapping, response));
    const valid = uniqueCreateMappings && uniqueDeleteMappings && createsMatch && deletesMatch && identitiesMatch;

    return valid;
  }

  /** Confirms one create mapping is represented by an authoritative item. */
  private isCreatedMappingAuthoritative(
    mapping: OfferRuleWorkflowCommandStatusResponse['created'][number],
    response: OfferRuleWorkflowCommandStatusResponse
  ): boolean
  {
    const item = response.items.find((candidate) => candidate.ruleId === mapping.ruleId);
    let valid = item !== undefined && item.version === mapping.version;
    if (item?.type === 'offer_set') {
      valid = valid && item.offerSet.generatedOfferSetId === mapping.generatedOfferSetId;
    }
    if (item?.type === 'dollar_discount') {
      valid = valid && mapping.generatedOfferSetId === null;
    }

    return valid;
  }

  /** Applies a text search to both visibility groups. */
  onSearch(searchTerm: string): void
  {
    this.searchValue = searchTerm;
    this.applyFilters();
  }

  /** Applies an allow-listed rule-type filter. */
  onRuleTypeFilterChange(filterValue: OfferRuleTypeFilter): void
  {
    if (this.isValidRuleTypeFilter(filterValue)) {
      this.selectedRuleTypeFilter = filterValue;
      this.applyFilters();
    }
  }

  /** Opens the completed modal for a fresh local rule. */
  openCreateDialog(event: Event): void
  {
    if (!this.getRuleMutationDisabled()) {
      this.captureModalLaunchElement(event);
      this.dialogMode = createMode;
      this.dialogTitle = 'Create Offer Set Rule';
      this.selectedClientKey = null;
      this.selectedRule = null;
      this.selectedDraft = null;
      this.dialogVisible = true;
    }
  }

  /** Opens a copied row and same-session envelope for editing. */
  openEditDialog(row: EditableOfferRule, event: Event): void
  {
    const currentRow = this.workingRowsByClientKey.get(row.clientKey);
    const canEdit = currentRow !== undefined
      && currentRow.changeState !== ruleChangeStates.deleted
      && !this.getRuleMutationDisabled();
    if (canEdit && currentRow) {
      this.captureModalLaunchElement(event);
      this.dialogMode = editMode;
      this.dialogTitle = 'Edit Offer Set Rule';
      this.selectedClientKey = currentRow.clientKey;
      this.selectedRule = cloneOfferRule(currentRow.rule);
      this.selectedDraft = this.getCopiedEnvelope(currentRow.clientKey);
      this.dialogVisible = true;
    }
  }

  /** Closes modal state without mutating rows or envelopes. */
  closeDialog(): void
  {
    this.dialogVisible = false;
    this.selectedClientKey = null;
    this.selectedRule = null;
    this.selectedDraft = null;
    this.restoreModalLaunchFocus();
  }

  /** Validates and applies one copied staged envelope. */
  handleDialogStaged(stagedDraft: EnhancedRuleDraft): void
  {
    let wasStaged = false;
    if (!this.getRuleMutationDisabled() && this.dialogVisible) {
      const normalizedDraft = this.getValidatedStagedDraft(stagedDraft);
      if (normalizedDraft !== null) {
        wasStaged = this.applyStagedDraft(normalizedDraft);
      }
    }
    if (wasStaged) {
      this.closeDialog();
    }
    if (!wasStaged) {
      this.showStageError();
    }
  }

  /** Confirms and stages a local deletion. */
  deleteRule(row: EditableOfferRule): void
  {
    const currentRow = this.workingRowsByClientKey.get(row.clientKey);
    const canDelete = currentRow !== undefined
      && currentRow.changeState !== ruleChangeStates.deleted
      && !this.getRuleMutationDisabled();
    if (canDelete) {
      this.confirmationService.confirm({
        message: deleteConfirmationMessage,
        header: 'Delete Rule',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          if (!this.getRuleMutationDisabled()) {
            this.applyLocalDelete(row.clientKey);
          }
        }
      });
    }
  }

  /** Restores a deleted persisted row from the authoritative baseline. */
  undoDelete(row: EditableOfferRule): void
  {
    const currentRow = this.workingRowsByClientKey.get(row.clientKey);
    if (currentRow && currentRow.changeState === ruleChangeStates.deleted && !this.getRuleMutationDisabled()) {
      this.restorePersistedRow(row.clientKey);
    }
  }

  /** Reverts a created or updated row and its same-session envelope. */
  revertRule(row: EditableOfferRule): void
  {
    const currentRow = this.workingRowsByClientKey.get(row.clientKey);
    if (currentRow && !this.getRuleMutationDisabled()) {
      if (currentRow.changeState === ruleChangeStates.created) {
        this.workingRowsByClientKey.delete(row.clientKey);
        this.envelopesByClientKey.delete(row.clientKey);
        this.refreshDerivedState();
      }
      if (currentRow.changeState === ruleChangeStates.updated) {
        this.restorePersistedRow(row.clientKey);
      }
    }
  }

  /** Provides an accessible label for local change state. */
  getChangeStateText(changeState: RuleChangeState): string
  {
    let label = '';
    if (changeState === ruleChangeStates.created) {
      label = 'New';
    }
    if (changeState === ruleChangeStates.updated) {
      label = 'Modified';
    }
    if (changeState === ruleChangeStates.deleted) {
      label = 'Pending deletion';
    }

    return label;
  }

  /** Builds a stable display name without loading legacy modal options. */
  getRuleName(rule: OfferRuleModel): string
  {
    let name = this.buildDollarDiscountRuleName(rule);
    const offerSetId = this.normalizeNumber(rule.offerSetId);
    if (offerSetId !== null) {
      name = this.buildOfferSetRuleName(rule);
    }

    return name;
  }

  /** Builds the identity and amount-based name for a Dollar Discount Rule. */
  private buildDollarDiscountRuleName(rule: OfferRuleModel): string
  {
    const ruleId = this.normalizeNumber(rule.id);
    const discountAmount = this.normalizeNumber(rule.dollarAmountDiscount);
    let identity = 'New';
    let amount = '';
    if (ruleId !== null) {
      identity = String(ruleId);
    }
    if (discountAmount !== null) {
      amount = String(discountAmount);
    }
    const name = `${identity} - $${amount} Discount`;

    return name;
  }

  /** Builds the vehicle-based name for an Offer Set Rule. */
  private buildOfferSetRuleName(rule: OfferRuleModel): string
  {
    const criteria = [
      this.normalizeCondition(rule.vehicleCondition),
      rule.year,
      rule.make,
      rule.model,
      rule.trim
    ].filter((value) => value !== null && value !== undefined && value !== '');
    const ruleId = this.normalizeNumber(rule.id);
    let identity = 'New';
    if (ruleId !== null) {
      identity = String(ruleId);
    }
    const name = `${identity} - ${criteria.join(' ')}`;

    return name;
  }

  /** Builds the existing offer-set editor link for persisted source identity. */
  getOfferSetLink(rule: OfferRuleModel): string
  {
    let link = '';
    const offerSetId = this.normalizeNumber(rule.offerSetId);
    if (offerSetId !== null) {
      link = `/new-specials/${this.accountId}#/specials/edit/${offerSetId}`;
    }

    return link;
  }

  /** Builds display tags from current rule criteria. */
  getVehicleCriteriaTags(rule: OfferRuleModel): string[]
  {
    const tags = [this.normalizeCondition(rule.vehicleCondition)];
    this.addCriteriaTag(tags, rule.year);
    this.addCriteriaTag(tags, rule.make);
    this.addCriteriaTag(tags, rule.model);
    this.addCriteriaTag(tags, rule.trim);

    return tags;
  }

  /** Resolves active and inactive status styling. */
  getRuleStatusClass(rule: OfferRuleModel): string
  {
    let statusClass = 'inactive';
    if (this.normalizeStatus(rule.status) === activeStatus) {
      statusClass = 'active';
    }

    return statusClass;
  }

  /** Resolves an explicit status label. */
  getRuleStatusLabel(rule: OfferRuleModel): string
  {
    const label = this.normalizeStatus(rule.status);

    return label;
  }

  /** Formats the newest valid audit timestamp. */
  getLatestTimestampDisplay(rule: OfferRuleModel): string
  {
    let display = '—';
    let timestamp = rule.modified;
    if (!this.isValidTimestamp(timestamp)) {
      timestamp = rule.created;
    }
    if (this.isValidTimestamp(timestamp)) {
      const date = new Date(String(timestamp));
      display = date.toLocaleString();
    }

    return display;
  }

  /** Resets and reloads when account ownership changes. */
  private handleAccountChange(accountValue: number): void
  {
    const isNewAccount = this.loadedAccountId !== accountValue;
    if (isNewAccount) {
      this.accountLoadSequence += 1;
      this.loadedAccountId = null;
      this.resetState();
      if (this.isValidAccountId(accountValue)) {
        this.loadedAccountId = accountValue;
        this.loadRules(accountValue, this.accountLoadSequence);
      }
    }
  }

  /** Clears all account-owned rule and modal state. */
  private resetState(): void
  {
    this.baselineById = new Map<number, EnhancedRuleDraft>();
    this.workingRowsByClientKey = new Map<string, EditableOfferRule>();
    this.envelopesByClientKey = new Map<string, EnhancedRuleDraft>();
    this.commandError = null;
    this.hasVersionConflict = false;
    this.conflictSnapshot = [];
    this.searchValue = '';
    this.selectedRuleTypeFilter = allRuleTypes;
    this.tableLoading = false;
    this.hasLoaded = false;
    this.hasRuleLoadFailed = false;
    this.dialogVisible = false;
    this.selectedClientKey = null;
    this.selectedRule = null;
    this.selectedDraft = null;
    this.refreshDerivedState(true);
  }

  /** Loads complete authoritative aggregates for one validated account. */
  private loadRules(accountId: number, loadSequence: number): void
  {
    this.tableLoading = true;
    this.offerRuleWorkflowService.loadAggregates(accountId)
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (collection) => {
          this.handleRulesLoaded(collection, accountId, loadSequence);
        },
        error: () => {
          this.handleRulesLoadError(accountId, loadSequence);
        }
      });
  }

  /** Accepts only the current account load response. */
  private handleRulesLoaded(
    collection: OfferRuleWorkflowAggregateCollection,
    accountId: number,
    loadSequence: number
  ): void
  {
    if (this.isCurrentAccountLoad(accountId, loadSequence)) {
      const expectedAccount = collection.accountId === accountId;
      this.tableLoading = false;
      if (expectedAccount) {
        this.replaceAuthoritativeItems(collection.items);
        this.hasLoaded = true;
        this.hasRuleLoadFailed = false;
      }
      if (!expectedAccount) {
        this.hasLoaded = false;
        this.hasRuleLoadFailed = true;
        this.showLoadError();
      }
    }
  }

  /** Handles only a current account load failure. */
  private handleRulesLoadError(accountId: number, loadSequence: number): void
  {
    if (this.isCurrentAccountLoad(accountId, loadSequence)) {
      this.tableLoading = false;
      this.hasLoaded = false;
      this.hasRuleLoadFailed = true;
      this.showLoadError();
    }
  }

  /** Checks account, sequence, and component lifetime for a load. */
  private isCurrentAccountLoad(accountId: number, loadSequence: number): boolean
  {
    const isCurrent = this.loadedAccountId === accountId
      && this.accountId === accountId
      && this.accountLoadSequence === loadSequence;

    return isCurrent;
  }

  /** Rebuilds baselines, rows, and envelopes from aggregate items. */
  private replaceAuthoritativeItems(items: readonly OfferRuleWorkflowAggregateItem[]): void
  {
    this.closeDialogWithoutFocus();
    this.baselineById = new Map<number, EnhancedRuleDraft>();
    this.workingRowsByClientKey = new Map<string, EditableOfferRule>();
    this.envelopesByClientKey = new Map<string, EnhancedRuleDraft>();
    for (const item of items) {
      this.addLoadedAggregate(item);
    }
    this.hasLoaded = true;
    this.hasRuleLoadFailed = false;
    this.tableLoading = false;
    this.refreshDerivedState(true);
  }

  /** Adds one complete clean persisted aggregate. */
  private addLoadedAggregate(item: OfferRuleWorkflowAggregateItem): void
  {
    const draft = mapAggregateItemToEnhancedRuleDraft(item, this.accountId);
    const clientKey = this.buildPersistedClientKey(item.ruleId);
    this.baselineById.set(item.ruleId, cloneEnhancedRuleDraft(draft));
    this.envelopesByClientKey.set(clientKey, cloneEnhancedRuleDraft(draft));
    const editableRow = this.buildEditableRow(clientKey, cloneOfferRule(draft.rule), ruleChangeStates.clean);
    this.workingRowsByClientKey.set(clientKey, editableRow);
  }

  /** Applies a validated create or edit to exactly one stable row. */
  private applyStagedDraft(draft: EnhancedRuleDraft): boolean
  {
    let wasApplied = false;
    if (this.dialogMode === createMode) {
      this.addCreatedDraft(draft);
      wasApplied = true;
    }
    if (this.dialogMode === editMode) {
      wasApplied = this.applyEditedDraft(draft);
    }

    return wasApplied;
  }

  /** Adds a new rule and copied envelope under one cryptographic key. */
  private addCreatedDraft(draft: EnhancedRuleDraft): void
  {
    const clientKey = this.generateDraftClientKey();
    const createdRule = this.cloneWritableRule(draft.rule);
    createdRule.accountId = this.accountId;
    const retainedDraft = this.buildRetainedEnvelope(draft, createdRule);
    const editableRow = this.buildEditableRow(clientKey, createdRule, ruleChangeStates.created);
    this.workingRowsByClientKey.set(clientKey, editableRow);
    this.envelopesByClientKey.set(clientKey, retainedDraft);
    this.refreshDerivedState();
  }

  /** Applies an edit to the selected stable row without reallocating identity. */
  private applyEditedDraft(draft: EnhancedRuleDraft): boolean
  {
    let wasApplied = false;
    if (this.selectedClientKey !== null) {
      const existingRow = this.workingRowsByClientKey.get(this.selectedClientKey);
      if (existingRow && existingRow.changeState !== ruleChangeStates.deleted) {
        const editedRule = this.mergeEditedRule(existingRow.rule, draft.rule);
        const retainedDraft = this.buildRetainedEnvelope(draft, editedRule);
        const changeState = this.resolveEditedChangeState(existingRow, retainedDraft);
        const editableRow = this.buildEditableRow(existingRow.clientKey, editedRule, changeState);
        this.workingRowsByClientKey.set(existingRow.clientKey, editableRow);
        this.envelopesByClientKey.set(existingRow.clientKey, retainedDraft);
        this.refreshDerivedState();
        wasApplied = true;
      }
    }

    return wasApplied;
  }

  /** Preserves current identity and audit fields while replacing writable data. */
  private mergeEditedRule(currentRule: OfferRuleModel, stagedRule: OfferRuleModel): OfferRuleModel
  {
    const mergedRule = this.cloneWritableRule(stagedRule);
    mergedRule.accountId = this.accountId;
    mergedRule.id = currentRule.id;
    mergedRule.created = currentRule.created;
    mergedRule.modified = currentRule.modified;

    return mergedRule;
  }

  /** Resolves created, updated, or clean from complete aggregate equality. */
  private resolveEditedChangeState(row: EditableOfferRule, editedDraft: EnhancedRuleDraft): RuleChangeState
  {
    let changeState: RuleChangeState = ruleChangeStates.created;
    if (row.changeState !== ruleChangeStates.created) {
      changeState = ruleChangeStates.updated;
      const baseline = this.getBaselineForRow(row);
      if (baseline && areEnhancedRuleDraftsEqual(baseline, editedDraft)) {
        changeState = ruleChangeStates.clean;
      }
    }

    return changeState;
  }

  /** Removes a new row or marks a persisted row deleted. */
  private applyLocalDelete(clientKey: string): void
  {
    const row = this.workingRowsByClientKey.get(clientKey);
    if (row) {
      if (row.changeState === ruleChangeStates.created) {
        this.workingRowsByClientKey.delete(clientKey);
        this.envelopesByClientKey.delete(clientKey);
      }
      if (row.changeState !== ruleChangeStates.created) {
        const editableRow = this.buildEditableRow(clientKey, cloneOfferRule(row.rule), ruleChangeStates.deleted);
        this.workingRowsByClientKey.set(clientKey, editableRow);
      }
      this.refreshDerivedState();
    }
  }

  /** Restores a persisted row exactly from its loaded baseline. */
  private restorePersistedRow(clientKey: string): void
  {
    const row = this.workingRowsByClientKey.get(clientKey);
    if (row) {
      const baseline = this.getBaselineForRow(row);
      if (baseline) {
        const restoredDraft = cloneEnhancedRuleDraft(baseline);
        const editableRow = this.buildEditableRow(clientKey, cloneOfferRule(restoredDraft.rule), ruleChangeStates.clean);
        this.workingRowsByClientKey.set(clientKey, editableRow);
        this.envelopesByClientKey.set(clientKey, restoredDraft);
        this.refreshDerivedState();
      }
    }
  }

  /** Builds an isolated editable row. */
  private buildEditableRow(
    clientKey: string,
    rule: OfferRuleModel,
    changeState: RuleChangeState
  ): EditableOfferRule
  {
    const editableRow: EditableOfferRule = {
      clientKey,
      rule,
      changeState,
      vehicleCriteriaTags: this.getVehicleCriteriaTags(rule)
    };

    return editableRow;
  }

  /** Recomputes groups, filters, operations, validation, and dirty state. */
  private refreshDerivedState(forceDirtyStateEmission = false): void
  {
    const rows = Array.from(this.workingRowsByClientKey.values());
    this.publicRules = rows.filter((row) => !this.normalizeBoolean(row.rule.isPrivate));
    this.privateRules = rows.filter((row) => this.normalizeBoolean(row.rule.isPrivate));
    this.applyFilters();
    this.deriveChangeSet(rows);
    const hasChanges = rows.some((row) => row.changeState !== ruleChangeStates.clean);
    if (hasChanges !== this.hasUnsavedRuleChanges || forceDirtyStateEmission) {
      this.hasUnsavedRuleChanges = hasChanges;
      this.hasUnsavedRuleChangesChange.emit(hasChanges);
    }
  }

  /** Derives complete versioned operations from retained envelopes. */
  private deriveChangeSet(rows: EditableOfferRule[]): void
  {
    const changeSet = this.createEmptyChangeSet();
    const errors: string[] = [];
    let operationCount = 0;
    for (const row of rows) {
      if (row.changeState !== ruleChangeStates.clean) {
        operationCount += 1;
        this.addRowOperation(row, changeSet, errors);
      }
    }
    if (operationCount > maximumBulkOperations) {
      errors.push(`A maximum of ${maximumBulkOperations} rule changes can be saved at once.`);
    }
    this.addDuplicateOperationErrors(changeSet, errors);
    this.changeSet = changeSet;
    this.changeSetValidation = {
      isValid: errors.length === 0 && operationCount > 0,
      operationCount,
      errors
    };
  }

  /** Adds one complete operation from its retained envelope. */
  private addRowOperation(
    row: EditableOfferRule,
    changeSet: OfferRuleWorkflowOperations,
    errors: string[]
  ): void
  {
    const draft = this.envelopesByClientKey.get(row.clientKey);
    try {
      if (draft && row.changeState === ruleChangeStates.created) {
        changeSet.creates.push(mapDraftToCreateOperation(row.clientKey, draft));
      }
      if (draft && row.changeState === ruleChangeStates.updated) {
        changeSet.updates.push(mapDraftToUpdateOperation(draft));
      }
      if (draft && row.changeState === ruleChangeStates.deleted) {
        changeSet.deletes.push(mapDraftToDeleteOperation(draft));
      }
      if (!draft) {
        errors.push(`Rule ${row.clientKey} is missing its complete aggregate data.`);
      }
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      errors.push(`Rule ${row.clientKey} has invalid or incomplete values.`);
    }
  }

  /** Rejects duplicate keys, IDs, and overlapping update/delete targets. */
  private addDuplicateOperationErrors(changeSet: OfferRuleWorkflowOperations, errors: string[]): void
  {
    const clientKeys = changeSet.creates.map((operation) => operation.clientKey);
    const updateIds = changeSet.updates.map((operation) => operation.ruleId);
    const deleteIds = changeSet.deletes.map((operation) => operation.ruleId);
    if (new Set(clientKeys).size !== clientKeys.length) {
      errors.push('Duplicate create client keys are not allowed.');
    }
    if (new Set(updateIds).size !== updateIds.length || new Set(deleteIds).size !== deleteIds.length) {
      errors.push('Duplicate persisted rule IDs are not allowed.');
    }
    if (updateIds.some((ruleId) => deleteIds.includes(ruleId))) {
      errors.push('The same rule cannot be updated and deleted.');
    }
  }

  /** Finds the baseline associated with a persisted row. */
  private getBaselineForRow(row: EditableOfferRule): EnhancedRuleDraft | undefined
  {
    let baseline: EnhancedRuleDraft | undefined;
    const persistedId = this.normalizeNumber(row.rule.id);
    if (persistedId !== null) {
      baseline = this.baselineById.get(persistedId);
    }

    return baseline;
  }

  /** Copies the operation set before emitting it. */
  private cloneChangeSet(changeSet: OfferRuleWorkflowOperations): OfferRuleWorkflowOperations
  {
    const clone = structuredClone(changeSet);

    return clone;
  }

  /** Applies search and typed filtering to both visibility groups. */
  private applyFilters(): void
  {
    this.filteredPublicRules = this.filterRows(this.publicRules);
    this.filteredPrivateRules = this.filterRows(this.privateRules);
  }

  /** Filters copied rows by current search and type state. */
  private filterRows(rows: EditableOfferRule[]): EditableOfferRule[]
  {
    const normalizedSearch = this.searchValue.trim().toLowerCase();
    const filteredRows = rows.filter((row) => {
      const matchesType = this.rowMatchesRuleType(row);
      const matchesSearch = this.rowMatchesSearch(row, normalizedSearch);

      return matchesType && matchesSearch;
    });

    return filteredRows;
  }

  /** Checks one row against the typed filter. */
  private rowMatchesRuleType(row: EditableOfferRule): boolean
  {
    let matches = true;
    const draft = this.envelopesByClientKey.get(row.clientKey);
    if (this.selectedRuleTypeFilter !== allRuleTypes) {
      matches = draft?.ruleType === this.selectedRuleTypeFilter;
    }

    return matches;
  }

  /** Checks one row against normalized searchable values. */
  private rowMatchesSearch(row: EditableOfferRule, normalizedSearch: string): boolean
  {
    let matches = true;
    if (normalizedSearch !== '') {
      const values = [
        this.getRuleName(row.rule),
        row.rule.year,
        row.rule.make,
        row.rule.model,
        row.rule.trim,
        row.rule.vehicleCondition,
        row.rule.status,
        this.getChangeStateText(row.changeState)
      ];
      const searchableValue = values.map((value) => String(value ?? '')).join(' ').toLowerCase();
      matches = searchableValue.includes(normalizedSearch);
    }

    return matches;
  }

  /** Copies normalized writable values into a rule model. */
  private cloneWritableRule(rule: OfferRuleModel): OfferRuleModel
  {
    const normalized = this.normalizeWritableRule(rule);
    const clone = new OfferRuleModel();
    clone.accountId = this.accountId;
    this.applyNormalizedRule(clone, normalized);

    return clone;
  }

  /** Applies normalized writable fields without arbitrary spreading. */
  private applyNormalizedRule(rule: OfferRuleModel, normalized: NormalizedWritableOfferRule): void
  {
    rule.offerSetId = normalized.offerSetId ?? undefined;
    rule.year = normalized.year ?? undefined;
    rule.make = normalized.make ?? undefined;
    rule.model = normalized.model ?? undefined;
    rule.trim = normalized.trim ?? undefined;
    rule.maxSalePrice = normalized.maxSalePrice ?? undefined;
    rule.vehicleLimit = normalized.vehicleLimit ?? undefined;
    rule.vehicleCondition = normalized.vehicleCondition;
    rule.dollarAmountDiscount = normalized.dollarAmountDiscount ?? undefined;
    rule.status = normalized.status;
    rule.isPrivate = normalized.isPrivate;
  }

  /** Normalizes writable fields for equality and persistence. */
  private normalizeWritableRule(rule: OfferRuleModel): NormalizedWritableOfferRule
  {
    const normalizedRule: NormalizedWritableOfferRule = {
      offerSetId: this.normalizeNumber(rule.offerSetId),
      year: this.normalizeNumber(rule.year),
      make: this.normalizeString(rule.make),
      model: this.normalizeString(rule.model),
      trim: this.normalizeString(rule.trim),
      maxSalePrice: this.normalizeNumber(rule.maxSalePrice),
      vehicleLimit: this.normalizeNumber(rule.vehicleLimit),
      vehicleCondition: this.normalizeCondition(rule.vehicleCondition),
      dollarAmountDiscount: this.normalizeNumber(rule.dollarAmountDiscount),
      status: this.normalizeStatus(rule.status),
      isPrivate: this.normalizeBoolean(rule.isPrivate)
    };

    return normalizedRule;
  }

  /** Compares only the existing writable rule fields. */
  private areWritableRulesEqual(firstRule: OfferRuleModel, secondRule: OfferRuleModel): boolean
  {
    const first = this.normalizeWritableRule(firstRule);
    const second = this.normalizeWritableRule(secondRule);
    const equal = first.offerSetId === second.offerSetId
      && first.year === second.year
      && first.make === second.make
      && first.model === second.model
      && first.trim === second.trim
      && first.maxSalePrice === second.maxSalePrice
      && first.vehicleLimit === second.vehicleLimit
      && first.vehicleCondition === second.vehicleCondition
      && first.dollarAmountDiscount === second.dollarAmountDiscount
      && first.status === second.status
      && first.isPrivate === second.isPrivate;

    return equal;
  }

  /** Copies and validates a modal envelope without trusting event identity. */
  private getValidatedStagedDraft(draft: EnhancedRuleDraft): EnhancedRuleDraft | null
  {
    let validatedDraft: EnhancedRuleDraft | null = null;
    try {
      const copiedDraft = cloneEnhancedRuleDraft(draft);
      const normalizedDraft = normalizeEnhancedRuleDraft(copiedDraft);
      if (this.isValidStagedDraft(normalizedDraft)) {
        normalizedDraft.rule.accountId = this.accountId;
        validatedDraft = cloneEnhancedRuleDraft(normalizedDraft);
      }
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
    }

    return validatedDraft;
  }

  /** Checks current account, modal selection, and typed envelope consistency. */
  private isValidStagedDraft(draft: EnhancedRuleDraft): boolean
  {
    const validAccount = this.isValidAccountId(this.accountId) && draft.rule.accountId === this.accountId;
    const validSelection = this.isValidModalSelection();
    let validType = false;
    if (draft.ruleType === OfferRuleTypeEnumerator.OfferSetRule) {
      validType = this.isValidOfferSetDraft(draft);
    }
    if (draft.ruleType === OfferRuleTypeEnumerator.DollarDiscountRule) {
      validType = this.isValidDollarDiscountDraft(draft);
    }
    const isValid = validAccount && validSelection && validType;

    return isValid;
  }

  /** Validates current modal identity against list state. */
  private isValidModalSelection(): boolean
  {
    let isValid = this.dialogMode === createMode && this.selectedClientKey === null;
    if (this.dialogMode === editMode && this.selectedClientKey !== null) {
      const row = this.workingRowsByClientKey.get(this.selectedClientKey);
      isValid = row !== undefined && row.changeState !== ruleChangeStates.deleted;
    }

    return isValid;
  }

  /** Validates matching Offer Set envelope identities. */
  private isValidOfferSetDraft(draft: EnhancedRuleDraft): boolean
  {
    const ruleOfferSetId = this.normalizeNumber(draft.rule.offerSetId);
    const sourceOfferSetId = this.normalizeNumber(draft.source?.offerSetId);
    const generatedOfferSetId = this.normalizeNumber(draft.serverMetadata?.generatedOfferSetId);
    const validCreate = draft.serverMetadata === null
      && ruleOfferSetId !== null
      && sourceOfferSetId === ruleOfferSetId
      && draft.source !== null;
    const validPersisted = draft.serverMetadata !== null
      && ruleOfferSetId !== null
      && generatedOfferSetId === ruleOfferSetId
      && draft.source === null;
    const isValid = (validCreate || validPersisted) && draft.generatedOfferSet !== null;

    return isValid;
  }

  /** Validates normalized Dollar Discount envelope state. */
  private isValidDollarDiscountDraft(draft: EnhancedRuleDraft): boolean
  {
    const discount = this.normalizeNumber(draft.rule.dollarAmountDiscount);
    const isValid = discount !== null
      && discount > 0
      && draft.rule.isPrivate === true
      && this.normalizeNumber(draft.rule.offerSetId) === null
      && draft.source === null
      && draft.generatedOfferSet === null;

    return isValid;
  }

  /** Builds a deep-copied envelope whose rule exactly matches its row. */
  private buildRetainedEnvelope(draft: EnhancedRuleDraft, rowRule: OfferRuleModel): EnhancedRuleDraft
  {
    const retainedDraft = cloneEnhancedRuleDraft(draft);
    retainedDraft.rule = cloneOfferRule(rowRule);
    const normalizedDraft = normalizeEnhancedRuleDraft(retainedDraft);

    return cloneEnhancedRuleDraft(normalizedDraft);
  }

  /** Returns a new deep copy of one retained same-session envelope. */
  private getCopiedEnvelope(clientKey: string): EnhancedRuleDraft | null
  {
    let copiedEnvelope: EnhancedRuleDraft | null = null;
    const retainedEnvelope = this.envelopesByClientKey.get(clientKey);
    if (retainedEnvelope) {
      copiedEnvelope = cloneEnhancedRuleDraft(retainedEnvelope);
    }

    return copiedEnvelope;
  }

  /** Reconciles envelopes using persisted IDs and explicit create mappings only. */
  private reconcileAuthoritativeEnvelopes(response: BulkOfferRuleResponse): Map<string, EnhancedRuleDraft>
  {
    const reconciled = new Map<string, EnhancedRuleDraft>();
    const createdDraftKeysById = this.buildCreatedDraftKeysById(response);
    for (const rule of response.rules) {
      const persistedId = Number(rule.id);
      const persistedKey = this.buildPersistedClientKey(persistedId);
      let retainedEnvelope = this.envelopesByClientKey.get(persistedKey);
      const createdDraftKey = createdDraftKeysById.get(persistedId);
      if (!retainedEnvelope && createdDraftKey) {
        retainedEnvelope = this.envelopesByClientKey.get(createdDraftKey);
      }
      if (retainedEnvelope) {
        const authoritativeEnvelope = this.buildRetainedEnvelope(retainedEnvelope, cloneOfferRule(rule));
        reconciled.set(persistedKey, authoritativeEnvelope);
      }
    }

    return reconciled;
  }

  /** Builds only unambiguous create-client-key mappings from the response. */
  private buildCreatedDraftKeysById(response: BulkOfferRuleResponse): Map<number, string>
  {
    const draftKeysById = new Map<number, string>();
    const ambiguousIds = new Set<number>();
    const persistedIdsByDraftKey = new Map<string, number>();
    const ambiguousDraftKeys = new Set<string>();
    for (const createdRule of response.created) {
      const validId = this.isValidIdentifier(createdRule.id);
      const validClientKey = createdRule.clientKey.startsWith(draftClientKeyPrefix)
        && this.envelopesByClientKey.has(createdRule.clientKey);
      if (validId && validClientKey && draftKeysById.has(createdRule.id)) {
        ambiguousIds.add(createdRule.id);
      }
      if (validId && validClientKey && persistedIdsByDraftKey.has(createdRule.clientKey)) {
        const mappedId = persistedIdsByDraftKey.get(createdRule.clientKey);
        if (mappedId !== createdRule.id) {
          ambiguousDraftKeys.add(createdRule.clientKey);
        }
      }
      if (validId && validClientKey && !draftKeysById.has(createdRule.id)) {
        draftKeysById.set(createdRule.id, createdRule.clientKey);
        persistedIdsByDraftKey.set(createdRule.clientKey, createdRule.id);
      }
    }
    for (const ambiguousId of ambiguousIds) {
      draftKeysById.delete(ambiguousId);
    }
    for (const [persistedId, draftKey] of draftKeysById) {
      if (ambiguousDraftKeys.has(draftKey)) {
        draftKeysById.delete(persistedId);
      }
    }

    return draftKeysById;
  }

  /** Generates a collision-resistant local draft key. */
  private generateDraftClientKey(): string
  {
    let clientKey = '';
    do {
      clientKey = `${draftClientKeyPrefix}${this.createCryptographicUuid()}`;
    } while (this.workingRowsByClientKey.has(clientKey));

    return clientKey;
  }

  /** Generates a cryptographically random version-four UUID. */
  private createCryptographicUuid(): string
  {
    const bytes = new Uint8Array(uuidByteLength);
    globalThis.crypto.getRandomValues(bytes);
    bytes[uuidVersionIndex] = (bytes[uuidVersionIndex] & uuidVersionMask) | uuidVersionValue;
    bytes[uuidVariantIndex] = (bytes[uuidVariantIndex] & uuidVariantMask) | uuidVariantValue;
    const segments: string[] = [];
    let byteIndex = 0;
    for (const segmentLength of uuidSegmentLengths) {
      segments.push(this.buildUuidSegment(bytes, byteIndex, segmentLength));
      byteIndex += segmentLength;
    }
    const uuid = segments.join('-');

    return uuid;
  }

  /** Encodes one UUID segment as hexadecimal text. */
  private buildUuidSegment(bytes: Uint8Array, startIndex: number, length: number): string
  {
    let segment = '';
    const endIndex = startIndex + length;
    for (let index = startIndex; index < endIndex; index += 1) {
      segment += bytes[index].toString(16).padStart(2, '0');
    }

    return segment;
  }

  /** Builds the deterministic persisted row key. */
  private buildPersistedClientKey(persistedId: number): string
  {
    const clientKey = `${persistedClientKeyPrefix}${persistedId}`;

    return clientKey;
  }

  /** Normalizes finite numbers and blank optional values. */
  private normalizeNumber(value: number | string | null | undefined): number | null
  {
    let normalizedValue: number | null = null;
    const hasValue = value !== null && value !== undefined && value !== '';
    if (hasValue) {
      const numberValue = Number(value);
      if (Number.isFinite(numberValue)) {
        normalizedValue = numberValue;
      }
    }

    return normalizedValue;
  }

  /** Trims text and converts blank values to null. */
  private normalizeString(value: string | null | undefined): string | null
  {
    let normalizedValue: string | null = null;
    if (value !== null && value !== undefined) {
      const trimmedValue = value.trim();
      if (trimmedValue !== '') {
        normalizedValue = trimmedValue;
      }
    }

    return normalizedValue;
  }

  /** Maps vehicle condition to the current allow-list. */
  private normalizeCondition(value: string | null | undefined): string
  {
    let condition = 'Any';
    const normalizedValue = this.normalizeString(value);
    if (normalizedValue !== null) {
      const conditionMap: {[key: string]: string} = {
        new: 'New',
        used: 'Used',
        cpo: 'CPO',
        any: 'Any'
      };
      const mappedCondition = conditionMap[normalizedValue.toLowerCase()];
      if (mappedCondition) {
        condition = mappedCondition;
      }
    }

    return condition;
  }

  /** Maps rule status to the current allow-list. */
  private normalizeStatus(value: string | null | undefined): string
  {
    let status = 'Inactive';
    if (value === activeStatus) {
      status = activeStatus;
    }

    return status;
  }

  /** Converts nullable values to a strict boolean. */
  private normalizeBoolean(value: boolean | null | undefined): boolean
  {
    const normalizedValue = value === true;

    return normalizedValue;
  }

  /** Checks a positive safe account identity. */
  private isValidAccountId(accountId: number): boolean
  {
    const isValid = Number.isSafeInteger(accountId) && accountId > 0;

    return isValid;
  }

  /** Checks a positive safe persisted identity. */
  private isValidIdentifier(identifier: number | undefined): boolean
  {
    const isValid = Number.isSafeInteger(identifier) && Number(identifier) > 0;

    return isValid;
  }

  /** Checks persisted concurrency metadata. */
  private isValidTimestamp(value: string | undefined): boolean
  {
    let isValid = false;
    if (value) {
      isValid = !Number.isNaN(new Date(value).getTime());
    }

    return isValid;
  }

  /** Checks an incoming filter against the typed allow-list. */
  private isValidRuleTypeFilter(filter: OfferRuleTypeFilter): boolean
  {
    const isValid = filter === allRuleTypes
      || filter === OfferRuleTypeEnumerator.OfferSetRule
      || filter === OfferRuleTypeEnumerator.DollarDiscountRule;

    return isValid;
  }

  /** Adds a visible non-wildcard criterion tag. */
  private addCriteriaTag(tags: string[], value: string | number | null | undefined): void
  {
    const hasValue = value !== null && value !== undefined && value !== '' && value !== 'Any';
    if (hasValue) {
      tags.push(String(value));
    }
  }

  /** Captures the native modal launch control for focus restoration. */
  private captureModalLaunchElement(event: Event): void
  {
    this.modalLaunchElement = null;
    if (event.currentTarget instanceof HTMLElement) {
      this.modalLaunchElement = event.currentTarget;
    }
  }

  /** Returns focus to the control that launched the modal. */
  private restoreModalLaunchFocus(): void
  {
    const launchElement = this.modalLaunchElement;
    this.modalLaunchElement = null;
    if (launchElement) {
      globalThis.setTimeout(() => {
        launchElement.focus();
      }, focusDelayMilliseconds);
    }
  }

  /** Clears modal state during authoritative replacement without stale focus. */
  private closeDialogWithoutFocus(): void
  {
    this.dialogVisible = false;
    this.selectedClientKey = null;
    this.selectedRule = null;
    this.selectedDraft = null;
    this.modalLaunchElement = null;
  }

  /** Shows a generic rule-load failure. */
  private showLoadError(): void
  {
    this.messageService.add({severity: 'error', summary: 'Error', detail: genericLoadError});
  }

  /** Shows a generic invalid or stale stage failure. */
  private showStageError(): void
  {
    this.messageService.add({severity: 'error', summary: 'Unable to stage rule', detail: genericStageError});
  }

  /** Creates an empty operation set. */
  private createEmptyChangeSet(): OfferRuleWorkflowOperations
  {
    const changeSet: OfferRuleWorkflowOperations = {creates: [], updates: [], deletes: []};

    return changeSet;
  }

  /** Creates the initial no-operation validation state. */
  private createInitialValidation(): OfferRuleChangeSetValidation
  {
    const validation: OfferRuleChangeSetValidation = {
      isValid: false,
      operationCount: 0,
      errors: []
    };

    return validation;
  }
}
